import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineCommentsIcon from 'LuxKit/Icons/line/LineCommentsIcon'
import LinePhoneIcon from 'LuxKit/Icons/line/LinePhoneIcon'
import ClickableListItem from 'LuxKit/List/ClickableListItem'
import config from 'constants/config'
import { NO_24_7_SUPPORT_REGIONS } from 'constants/config/region'
import ContactContext from 'contexts/contactContext'
import { useRenderWhatsApp } from 'hooks/useRenderWhatsApp'
import { getSVGImageUrl } from 'lib/image/imageUtils'
import shouldRenderTimely, { getTimelyLink } from 'lib/timely/timely'
import type { ComponentProps, MouseEventHandler } from 'react'
import React, { useCallback, useContext } from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { isSupportAssistantEnabled } from 'selectors/featuresSelectors'

interface Props extends Pick<ComponentProps<typeof DropdownList>, 'anchorRef' | 'open' | 'onClose'> {
  currentRegionCode: string
  currentRegionName: string
  onItemClick: () => void
  supportAssistantEnabled: boolean
}

function NavigationHeaderDropdown(props: Props) {
  const intl = useIntl()
  const {
    currentRegionCode,
    currentRegionName,
    onItemClick,
    supportAssistantEnabled,
    open,
    anchorRef,
    onClose,
  } = props
  const currentContact = useContext(ContactContext)

  const localContact = currentContact.local
  const internationalContact = currentContact.international
  const shouldRenderWhatsApp = useRenderWhatsApp(currentRegionCode)

  const showTimelyPopup = useCallback<MouseEventHandler<HTMLButtonElement>>((event) => {
    event.preventDefault()
    onItemClick()
    window.open(getTimelyLink(), 'popup', 'width=600,fullscreen=no,location=no,menubar=no,toolbar=no')
  }, [onItemClick])

  return (
    <DropdownList
      size="S"
      open={open}
      onClose={onClose}
      anchorRef={anchorRef}
      triggerRef={anchorRef}
    >
      {!NO_24_7_SUPPORT_REGIONS.includes(currentRegionCode) && (
        <ClickableListItem
          size="medium"
          aria-label={intl.formatMessage(
            {
              id: 'BIiLPF1RtZ',
              defaultMessage: 'Local Sales & Support contact - {regionName}',
              description: 'Aria label for local sales and support contact in dropdown',
            },
            { regionName: currentRegionName },
          )}
          href={`tel:${localContact.number}`}
          startIcon={<LinePhoneIcon />}
          title={intl.formatMessage({
            id: 'VuL2uw26xF',
            defaultMessage: 'Local Sales & Support',
            description: 'Title for local sales and support contact in dropdown',
          })}
          onClick={onItemClick}
          rel="nofollow"
          subtitle={localContact.humanReadable}
        />
      )}
      {supportAssistantEnabled && <ClickableListItem
        size="medium"
        aria-label={intl.formatMessage(
          {
            id: 'C8vdLh6rTj',
            defaultMessage: 'Luxury Escapes Support Assistant - {regionName}',
            description: 'Aria label for support assistant contact in dropdown',
          },
          { regionName: currentRegionName },
        )}
        to="/support"
        startIcon={<LineCommentsIcon />}
        title={intl.formatMessage({
          id: 'n9dni8iHhf',
          defaultMessage: 'Chat now',
          description: 'Title for chat support option in contact dropdown',
        })}
        onClick={onItemClick}
        rel="nofollow"
        subtitle={intl.formatMessage({
          id: 'BzN1IBsayp',
          defaultMessage: 'Luxury Escapes Support Assistant',
          description: 'Subtitle for chat support showing assistant name in contact dropdown',
        })}
      />}
      {shouldRenderWhatsApp && <ClickableListItem
        size="medium"
        title={intl.formatMessage({
          id: '3VFHvLZzeQ',
          defaultMessage: 'WhatsApp',
          description: 'Title for WhatsApp contact option in navigation header dropdown',
        })}
        aria-label={intl.formatMessage(
          {
            id: 'YYMMVLnKN7',
            defaultMessage: 'WhatsApp - {regionName}',
            description: 'Aria label for WhatsApp contact in navigation header dropdown',
          },
          { regionName: currentRegionName },
        )}
        startIcon={<img
          src={getSVGImageUrl('whatsapp-logo')}
          width="24"
          height="24"
          alt={intl.formatMessage({
            id: '9bf7j0uVJt',
            defaultMessage: 'WhatsApp',
            description: 'Alt text for WhatsApp logo in navigation header dropdown',
          })}
        />}
        onClick={onItemClick}
        href={`https://wa.me/${config.WHATS_APP_NUMBER}`}
        target="_blank"
        rel="noopener"
      />}
      {shouldRenderTimely(currentRegionCode) && <ClickableListItem
        size="medium"
        title={intl.formatMessage({
          id: 'bi/sQ/dhSj',
          defaultMessage: 'Book store appointment',
          description: 'Title for book store appointment option in contact dropdown',
        })}
        aria-label={intl.formatMessage(
          {
            id: '7WwLh2VZyI',
            defaultMessage: 'Timely - {regionName}',
            description: 'Aria label for store appointment booking in contact dropdown',
          },
          { regionName: currentRegionName },
        )}
        startIcon={<img src={getSVGImageUrl('logo-LE')} width="24" height="24" alt="support" />}
        onClick={showTimelyPopup}
        href={getTimelyLink()}
        target="popup"
        rel="noopener"
      />}
      <ClickableListItem
        size="medium"
        startIcon={<LinePhoneIcon />}
        aria-label={intl.formatMessage({
          id: 'Ehfc60I/Ok',
          defaultMessage: 'International Support contact',
          description: 'Aria label for international support contact in dropdown',
        })}
        href={`tel:${internationalContact.number}`}
        title={intl.formatMessage({
          id: 'J1RshtIDp+',
          defaultMessage: 'International Support',
          description: 'Title for international support contact in dropdown',
        })}
        onClick={onItemClick}
        rel="nofollow"
        subtitle={internationalContact.humanReadable}
      />
    </DropdownList>
  )
}

const mapStateToProps = (state: App.State) => ({
  currentRegionCode: state.geo.currentRegionCode,
  currentRegionName: state.geo.currentRegionName,
  supportAssistantEnabled: isSupportAssistantEnabled(state),
})

export default connect(mapStateToProps)(NavigationHeaderDropdown)
